<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import type { Project } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';
  import { projectMoveActions, focusAccent } from '../../lib/projectStatusActions';
  import { openProjectRail } from '../../lib/stores/ui';
  import {
    getCachedTasks,
    subscribeToCachedTasks,
    fetchProjectTasks,
  } from '../../lib/stores/projectTasksCache';
  import { getTaskCounts } from '../../lib/useProjectTaskCounts';
  import { completeTask, addTask } from '../../lib/taskApi';
  import { priorityDotColors as priorityBorderColors, priorityOrder } from '../../lib/taskBoard';
  import ActionSheet from './ActionSheet.svelte';
  import MobileAddTask, { type NewTaskDraft } from './MobileAddTask.svelte';

  let { project }: { project: Project } = $props();
  // Cards are keyed by project.id. Subscription and fetch have the same lifetime.
  let cached = $state(untrack(() => getCachedTasks(project.id)));
  onMount(() => {
    const unsubscribe = subscribeToCachedTasks(project.id, (data) => {
      cached = data;
    });
    return unsubscribe;
  });
  // eslint-disable-next-line momentium/no-unguarded-effect -- conditional data fetch: invalidation makes fetched false; loading/fetched guards and request deduplication prevent a fetch loop
  $effect(() => {
    const projectId = project.id;
    if (!cached.fetched && !cached.loading) {
      untrack(() => void fetchProjectTasks(projectId));
    }
  });
  let counts = $derived(
    cached.fetched
      ? {
          total: cached.tasks.length,
          done: cached.tasks.filter((t) => t.status !== 'todo').length,
          todo: cached.tasks.filter((t) => t.status === 'todo').length,
        }
      : getTaskCounts(project),
  );
  let incomplete = $derived(
    cached.tasks
      .filter((t) => t.status === 'todo')
      .sort(
        (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority] || a.position - b.position,
      ),
  );
  let progress = $derived(counts.total ? Math.round((counts.done / counts.total) * 100) : 0);
  let menuOpen = $state(false);
  let actions = $derived([
    ...projectMoveActions(project),
    {
      label: project.isPriority ? 'Unstar' : 'Star',
      onSelect: () =>
        router.patch(`/projects/${project.id}/toggle_priority`, {}, { preserveScroll: true }),
    },
  ]);
  let accent = $derived(focusAccent(project.status));
  async function createTask(draft: NewTaskDraft) {
    await addTask(project.id, draft.title, cached.tasks, {
      notes: draft.notes,
      priority: draft.priority,
      tagNames: draft.tagNames,
    });
  }
</script>

<article class="focus-card" style:--accent={accent} aria-label={project.title}>
  <header>
    <button
      type="button"
      class="project-title"
      onclick={() => openProjectRail(project.id)}
      title={project.title}
    >
      <span aria-hidden="true"
        >{categoryIcons[project.category] || '📋'}{project.isPriority ? ' ★' : ''}</span
      >
      <span class="title">{project.title}</span>
    </button>
    <button
      type="button"
      class="menu"
      aria-label="Actions for {project.title}"
      onclick={() => (menuOpen = true)}>⋯</button
    >
  </header>
  <div class="progress-row">
    {#if project.multiWeek && project.totalWeeks}
      <span class="span-badge"
        >{project.weekIndex
          ? `${project.weekIndex}/${project.totalWeeks}`
          : `${project.totalWeeks}w`}</span
      >
    {/if}
    <div
      class="progress"
      role="progressbar"
      aria-label="Task progress"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={progress}
    >
      <span style:width="{progress}%"></span>
    </div>
    <span>{counts.done}/{counts.total}</span>
  </div>
  {#if !cached.fetched}
    <p class="message">Loading tasks…</p>
  {:else}
    {#each incomplete.slice(0, 3) as task (task.id)}
      <div class="task">
        <button
          type="button"
          class="check"
          aria-label="Complete {task.title}"
          onclick={() => completeTask(project.id, task.id, cached.tasks)}
          ><span style:border-color={priorityBorderColors[task.priority]}></span></button
        >
        <button type="button" class="task-title" onclick={() => openProjectRail(project.id)}
          >{task.title}</button
        >
      </div>
    {/each}
    {#if incomplete.length > 3}
      <button type="button" class="remaining" onclick={() => openProjectRail(project.id)}
        >View {incomplete.length - 3} more tasks ›</button
      >
    {:else if !incomplete.length}
      <p class="message">{counts.total ? 'All tasks completed' : 'No tasks yet'}</p>
    {/if}
  {/if}
  <footer>
    {#if cached.fetched}<MobileAddTask onSubmit={createTask} />{/if}
    <span>{counts.done} completed</span>
  </footer>
</article>
<ActionSheet open={menuOpen} onClose={() => (menuOpen = false)} title={project.title} {actions} />

<style>
  .focus-card {
    min-width: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-top: 3px solid var(--accent);
    border-radius: 12px;
    overflow: hidden;
  }
  header {
    display: flex;
    align-items: center;
    padding: 2px 4px 0 12px;
  }
  button {
    font: inherit;
    border: 0;
    background: none;
    cursor: pointer;
  }
  .project-title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    min-height: 44px;
    text-align: left;
    padding: 0;
    color: #111827;
    font-size: 16px;
    font-weight: 600;
  }
  .project-title > span:first-child {
    flex: none;
    font-size: 14px;
  }
  .title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .menu {
    width: 44px;
    height: 44px;
    flex: none;
    font-size: 22px;
    color: #64748b;
  }
  .progress-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 12px 10px;
    font-size: 12px;
    color: #64748b;
  }
  .span-badge {
    color: #2563eb;
    background: #eff6ff;
    border-radius: 4px;
    padding: 2px 6px;
    font-weight: 600;
  }
  .progress {
    flex: 1;
    height: 6px;
    border-radius: 4px;
    background: #f1f5f9;
    overflow: hidden;
  }
  .progress span {
    display: block;
    height: 100%;
    background: var(--accent);
  }
  .task {
    display: flex;
    align-items: center;
    border-top: 1px solid #f1f5f9;
    padding: 0 8px;
  }
  .check {
    display: grid;
    place-items: center;
    width: 44px;
    height: 48px;
    flex: none;
    padding: 0;
  }
  .check span {
    width: 22px;
    height: 22px;
    border: 2px solid;
    border-radius: 6px;
  }
  .check:disabled {
    opacity: 0.5;
    cursor: wait;
  }
  .task-title {
    flex: 1;
    min-height: 48px;
    min-width: 0;
    padding: 8px 4px;
    text-align: left;
    font-size: 14px;
    color: #334155;
    overflow-wrap: anywhere;
  }
  .remaining {
    min-height: 44px;
    padding: 0 16px;
    color: #2563eb;
    font-size: 14px;
  }
  .message {
    padding: 4px 16px;
    color: #64748b;
    font-size: 14px;
  }
  footer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    border-top: 1px solid #f1f5f9;
  }
  footer :global(.add-task-trigger) {
    flex: 1;
    justify-content: flex-start;
    border: 0;
    background: none;
    padding-left: 0;
  }
  footer > span {
    flex: none;
    color: #64748b;
    font-size: 12px;
  }
  button:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
  }
</style>
